/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  ButtonProps,
  DIALOG_SIZE_SM,
  DialogContentProps,
  DialogProps,
} from '@squiz/sds';
import { COMPONENT_SERVICE_STRINGS } from 'apps/component-service/src/Constants/Strings.constants';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPONENT_DETAILS_DATA_IDS } from '../../ComponentDetails.constants';
import { DeleteAllComponentVersionsProps } from './DeleteAllComponentVersions.props';
import { DeleteAllComponentVersionsDialog } from './elements/DeleteAllComponentVersionsDialog';

/**
 * The button to delete all versions of a component.
 * */
export const DeleteAllComponentVersions = (props: DeleteAllComponentVersionsProps): JSX.Element => {
  const { t } = useTranslation();
  const { updateActiveDialog } = useDialogStore();
  const { componentNamespace, componentName, includedInSets } = props;

  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const labels = useMemo(() => {
    return {
      deleteAllComponentVersions: t(COMPONENT_SERVICE_STRINGS.deleteAllComponentVersions),
    };
  }, [t]);

  const setDeleteModalOpen = (isOpen: boolean): void => {
    updateActiveDialog(isOpen ? deleteDialogProps : null);
  };

  const deleteButtonProps: ButtonProps = {
    format: BUTTON_FORMAT_SOFT,
    onClick: () => setDeleteModalOpen(true),
    text: labels.deleteAllComponentVersions,
    theme: BUTTON_THEME_DEFAULT,
  };

  const deleteDialogProps: DialogProps = {
    dialogContent: {
      renderContent: (dialogContentProps: DialogContentProps): JSX.Element => {
        return (
          <DeleteAllComponentVersionsDialog
            componentName={componentName}
            componentNamespace={componentNamespace}
            includedInSets={includedInSets}
            {...dialogContentProps}
          />
        );
      },
    },
    dialogSize: DIALOG_SIZE_SM,
    heading: labels.deleteAllComponentVersions,
    onClose: () => setDeleteModalOpen(false),
    showDividers: true,
    stateHandler: useDialogStore,
  };

  return (
    <Button
      {...deleteButtonProps}
      data-testid={COMPONENT_DETAILS_DATA_IDS.deleteAllComponentsButton}
      ref={deleteButtonRef}
    />
  );
};
